import _ from 'underscore';
import axios from 'axios';
import AtlStorage from './atl-storage';
import HamletProductModel from './hamlet-product-model';
import ProductContentData from './hamlet-product-content-data';
import { hamletApi } from '../../common/hamlet/hamlet-api';



export default class HamletProducts {
  products: any[];
  options: any[];
  storage: AtlStorage;
  productContentData: ProductContentData;
  hamletProductsSessionKey: string;
  hamletProductsClearReasonSessionKey: string;

  constructor() {
    this.products = [];
    this.options  = [];
    this.storage = new AtlStorage();
    this.productContentData = new ProductContentData();

    this.hamletProductsSessionKey = 'hamlet.api.products';
    this.hamletProductsClearReasonSessionKey = 'hamlet.api.products.clear.reason';

    const lastProductsClearReason = this.storage.session.get(this.hamletProductsClearReasonSessionKey) || '';
    const currentProductsClearReason = 'PUR-8984';

    let stored = this.storage.session.get(this.hamletProductsSessionKey) || [];
    if (lastProductsClearReason !== currentProductsClearReason) {
      this.storage.session.remove(this.hamletProductsSessionKey);
      this.storage.session.set(this.hamletProductsClearReasonSessionKey, currentProductsClearReason);
      stored = [];
    }

    _.each(stored, (product) => {
      if (product.orderableItems.length > 0) {
        this.addProduct(product);
      }
    });

  }

  /*
   * Query Hamlet for the given products/licenseTypes. If the given products already
   * exist locally, then resolve with those products. Otherwise, hit the API, and
   * store the results in our local cache
   */
  queryProducts(providedOptions: { products: string[]; licenseTypes: string[]; monthsValid: number; }) {
    const options = {
      products: [],
      licenseTypes: [ 'commercial' ],
      ...providedOptions,
    };

    let params = {
      licenseType: options.licenseTypes,
      productKey: [],
      requestingApplication: 'WAC'
    };

    let cached   = [];

    if (_.has(options, 'monthsValid')) {
      params.monthsValid = [ options.monthsValid ];
    }

    /*
     * Loop through the provided productKeys and see which ones we have in the
     * cache already. Create a list for each
     */
    _.each(_.uniq(options.products), productKey => {

        let product = this.findByKey(productKey);

        if (!(product && product.hasTheseLicenses(options.licenseTypes))) {
          params.productKey.push(productKey);
        } else {
          cached.push(product);
        }
    });

    /*
     * If all the products are already in the cache, return the product
     * list from the cache
     *
     * Else
     * Hit the Hamlet API for the products not already cached. Concat the cached
     * ones we already had to the results
     */
    if (params.productKey.length === 0) {
      return Promise.resolve({
        products: cached
      });
    } else {

      const qstring = this.createQueryStringParamsForHamlet(params);
      const url = `${hamletApi.getHamletPath()}/1.0/public/pricing/query?${qstring}`;

      return axios.get(url)
        .then( result => {

          _.each(result.data.products, (product) => {
            cached.push(this.addProduct(product));
          });

          return Promise.resolve({
            products: cached
          });
        })
        .catch( e => {
          return Promise.reject(e);
        });
    }
  }

  createQueryStringParamsForHamlet(params) {
    let qStringParams = {};
    let qStringBuilder = [];

    _.each(params, function (value, key) {
      if (Array.isArray(value)) {
        qStringParams[key] = _.map(value, function (item) {
          return key + '=' + item;
        });
      } else {
        qStringParams[key] = [key + '=' + value];
      }
    });

    _.each(qStringParams, (item) => {
      qStringBuilder.push(item);
    });

    return _.flatten(qStringBuilder).join('&');
  }

  findByKey(key: string) {
    return _.where (this.products, { productKey: key })[0];
  }

  /*
   * Add a new product to the list. If the product already exists in the list, check each
   * orderable to make sure all orderable items are accounted for. Once done, save to
   * local storage for later retrieval to prevent redundant API calls
   */
  addProduct(product) {

    const existing = this.findByKey(product.productKey);
    if (!existing) {
      let p = new HamletProductModel(product, this.productContentData);
      this.products.push(p);
    } else {
      for (var i=0,l=product.orderableItems.length; i<l; i+=1) {
        if (!existing.hasOrderable(product.orderableItems[i].orderableItemId)) {
          existing.addOrderable(product.orderableItems[i]);
        }
      }
    }
    this.storeProducts();
    return this.findByKey(product.productKey);
  }

  storeProducts() {
    this.storage.session.set(this.hamletProductsSessionKey, this.products);
  }

}
